import React, { useCallback, useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'multi_io_view_mode';
const VIEW_MODE_EVENT = 'multi-io-view-mode-change';
export const MULTI_IO_SHORT_LABEL = 'I/O';
export const MULTI_IO_TABLE_MIN_VIEWPORT_WIDTH = 1500;

function getInfoSurfaceColor(theme) {
  return (
    theme.palette.innerContainer?.background ??
    theme.palette.table?.background ??
    theme.palette.background.paper
  );
}

const useStyles = makeStyles((theme) => ({
  toggle: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  tableContainer: {
    backgroundColor: getInfoSurfaceColor(theme),
    color: theme.palette.fontcolor ?? theme.palette.text.primary,
    width: '100%',
    overflowX: 'auto'
  },
  table: {
    '&&': {
      backgroundColor: getInfoSurfaceColor(theme)
    },
    tableLayout: 'fixed',
    '& th, & td': {
      boxSizing: 'border-box',
      color: 'inherit',
      padding: theme.spacing(0.75, 1),
      verticalAlign: 'middle'
    },
    '&& thead th, && tbody td': {
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '& th:first-child, & td:first-child': {
      whiteSpace: 'nowrap'
    },
    '& tbody td:not(:last-child), & tbody td:not(:last-child) .MuiTypography-root, & tbody td:not(:last-child) .MuiInputBase-root': {
      fontSize: theme.typography.body2.fontSize
    },
    '& tbody td .MuiTypography-gutterBottom': {
      marginBottom: 0
    },
    '& .MuiInputBase-root': {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%'
    }
  }
}));

function readStoredViewMode() {
  if (typeof window === 'undefined') return 'table';

  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'grid' ? 'grid' : 'table';
  } catch (_error) {
    return 'table';
  }
}

export function getEffectiveMultiIOViewMode(viewMode, forceGrid) {
  return forceGrid ? 'grid' : viewMode === 'grid' ? 'grid' : 'table';
}

export function useMultiIOViewMode() {
  const narrowScreen = useMediaQuery(
    `(max-width:${MULTI_IO_TABLE_MIN_VIEWPORT_WIDTH}px)`
  );
  const [viewMode, setViewModeState] = useState(readStoredViewMode);

  useEffect(() => {
    const syncViewMode = (event) => setViewModeState(event.detail);
    window.addEventListener(VIEW_MODE_EVENT, syncViewMode);
    return () => window.removeEventListener(VIEW_MODE_EVENT, syncViewMode);
  }, []);

  const setViewMode = useCallback((nextViewMode) => {
    const normalizedViewMode = nextViewMode === 'table' ? 'table' : 'grid';
    setViewModeState(normalizedViewMode);

    try {
      window.sessionStorage.setItem(STORAGE_KEY, normalizedViewMode);
    } catch (_error) {
      // The preference remains available for the current mounted view.
    }

    window.dispatchEvent(
      new CustomEvent(VIEW_MODE_EVENT, { detail: normalizedViewMode })
    );
  }, []);

  const selectorAvailable = !isMobile && !narrowScreen;
  const effectiveViewMode = getEffectiveMultiIOViewMode(viewMode, !selectorAvailable);

  return {
    viewMode,
    setViewMode,
    selectorAvailable,
    showTable: effectiveViewMode === 'table'
  };
}

export function useMultiIOTableSort(initialKey = 'io') {
  const [sort, setSort] = useState({ key: initialKey, direction: 'asc' });

  const requestSort = useCallback((key) => {
    setSort((currentSort) => ({
      key,
      direction:
        currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  return { ...sort, requestSort };
}

export function sortMultiIORows(rows, sortKey, sortDirection, getSortValue) {
  const directionFactor = sortDirection === 'desc' ? -1 : 1;

  return rows
    .map((row, originalIndex) => ({ row, originalIndex }))
    .sort((left, right) => {
      const leftValue = getSortValue(left.row, sortKey);
      const rightValue = getSortValue(right.row, sortKey);

      if (leftValue == null && rightValue == null) {
        return left.originalIndex - right.originalIndex;
      }
      if (leftValue == null) return 1;
      if (rightValue == null) return -1;

      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, {
              numeric: true,
              sensitivity: 'base'
            });

      return comparison === 0
        ? left.originalIndex - right.originalIndex
        : comparison * directionFactor;
    })
    .map(({ row }) => row);
}

export function MultiIOViewToggle({ viewMode, onChange, hidden = false }) {
  const classes = useStyles();
  const { t } = useTranslation();

  if (hidden) return null;

  return (
    <div className={classes.toggle}>
      <ButtonGroup size="small" color="primary" aria-label={t('multi_io_view_style')}>
        <Button
          variant={viewMode === 'grid' ? 'contained' : 'outlined'}
          aria-pressed={viewMode === 'grid'}
          aria-label={t('multi_io_grid_view')}
          title={t('multi_io_grid_view')}
          onClick={() => onChange('grid')}
        >
          <ViewModuleIcon />
        </Button>
        <Button
          variant={viewMode === 'table' ? 'contained' : 'outlined'}
          aria-pressed={viewMode === 'table'}
          aria-label={t('multi_io_table_view')}
          title={t('multi_io_table_view')}
          onClick={() => onChange('table')}
        >
          <ViewListIcon />
        </Button>
      </ButtonGroup>
    </div>
  );
}

export function MultiIOTable({
  columns,
  children,
  minWidth = 760,
  ariaLabel,
  sortKey,
  sortDirection = 'asc',
  onSort
}) {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <TableContainer className={classes.tableContainer}>
      <Table
        className={classes.table}
        size="small"
        style={{ minWidth }}
        aria-label={ariaLabel}
      >
        <colgroup>
          {columns.map((column) => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                sortDirection={sortKey === column.key ? sortDirection : false}
                style={{
                  fontSize: theme.typography.body1.fontSize,
                  fontWeight: theme.typography.fontWeightRegular,
                  textTransform: 'capitalize'
                }}
              >
                {onSort && column.sortable !== false ? (
                  <TableSortLabel
                    active={sortKey === column.key}
                    direction={sortKey === column.key ? sortDirection : 'asc'}
                    onClick={() => onSort(column.key)}
                    style={{ fontWeight: theme.typography.fontWeightRegular }}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
  );
}
